import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentProps } from "react";

import { cn } from "@/shared/lib/cn";
import { Input } from "@/shared/ui/input";

type SaveState = "clean" | "dirty" | "saving" | "saved" | "error";

interface InlineSaveInputProps extends Omit<
  ComponentProps<typeof Input>,
  "defaultValue" | "onChange" | "value"
> {
  value: string;
  authoritativeRevision: number;
  onSave: (value: string) => Promise<{
    succeeded: boolean;
    committedRevision: number | null;
  }>;
  emptyMessage?: string;
  unknown?: boolean;
}

const statusLabels: Record<SaveState, string> = {
  clean: "",
  dirty: "UNSAVED",
  saving: "SAVING…",
  saved: "SAVED ✓",
  error: "NOT SAVED — TRY AGAIN",
};

export function InlineSaveInput({
  value,
  authoritativeRevision,
  onSave,
  emptyMessage,
  unknown = false,
  className,
  disabled,
  onBlur,
  onKeyDown,
  ...props
}: InlineSaveInputProps) {
  const [draft, setDraft] = useState(value);
  const [state, setState] = useState<SaveState>("clean");
  const draftRef = useRef(draft);
  const valueRef = useRef(value);
  const authoritativeRevisionRef = useRef(authoritativeRevision);
  const savingRef = useRef(false);
  const submittedRef = useRef<string | null>(null);
  const submittedBaseValueRef = useRef<string | null>(null);
  const committedRevisionRef = useRef<number | null>(null);
  const resaveRef = useRef(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  draftRef.current = draft;
  valueRef.current = value;
  authoritativeRevisionRef.current = authoritativeRevision;

  const markSaved = useCallback(() => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setState("saved");
    savedTimerRef.current = setTimeout(() => setState("clean"), 1400);
  }, []);

  const settleAuthoritative = useCallback((): boolean => {
    const submitted = submittedRef.current;
    const committedRevision = committedRevisionRef.current;
    if (
      !submitted ||
      committedRevision === null ||
      authoritativeRevisionRef.current < committedRevision
    ) {
      return false;
    }
    if (
      authoritativeRevisionRef.current === committedRevision &&
      valueRef.current === submittedBaseValueRef.current &&
      valueRef.current !== submitted
    ) {
      return false;
    }
    submittedRef.current = null;
    submittedBaseValueRef.current = null;
    committedRevisionRef.current = null;
    if (draftRef.current.trim() === submitted) {
      setDraft(valueRef.current);
      if (valueRef.current === submitted) markSaved();
      else setState("clean");
    } else {
      setState("dirty");
    }
    return true;
  }, [markSaved]);

  useEffect(() => {
    if (settleAuthoritative()) return;
    if (!submittedRef.current && (state === "clean" || state === "saved")) {
      setDraft(value);
    }
  }, [authoritativeRevision, settleAuthoritative, state, value]);

  useEffect(
    () => () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    },
    [],
  );

  const save = useCallback(
    async (nextValue: string): Promise<void> => {
      const submitted = nextValue.trim();
      if (!submitted) {
        setDraft(valueRef.current);
        setState("clean");
        return;
      }
      if (
        submittedRef.current === submitted &&
        committedRevisionRef.current !== null
      ) {
        return;
      }
      if (submitted === valueRef.current && !savingRef.current) {
        setDraft(submitted);
        setState("clean");
        return;
      }
      if (savingRef.current) {
        resaveRef.current = true;
        return;
      }
      savingRef.current = true;
      submittedRef.current = submitted;
      submittedBaseValueRef.current = valueRef.current;
      committedRevisionRef.current = null;
      setState("saving");
      const result = await onSave(submitted);
      savingRef.current = false;
      if (!result?.succeeded) {
        submittedRef.current = null;
        submittedBaseValueRef.current = null;
        committedRevisionRef.current = null;
        resaveRef.current = false;
        setState("error");
        return;
      }
      const newest = draftRef.current.trim();
      const shouldResave = resaveRef.current && newest !== submitted;
      resaveRef.current = false;
      if (shouldResave) {
        submittedRef.current = null;
        submittedBaseValueRef.current = null;
        committedRevisionRef.current = null;
        setState("dirty");
        await save(newest);
        return;
      }
      committedRevisionRef.current = result.committedRevision;
      if (result.committedRevision === null) {
        submittedRef.current = null;
        submittedBaseValueRef.current = null;
        if (newest === submitted) markSaved();
        else setState("dirty");
        return;
      }
      if (!settleAuthoritative() && newest !== submitted) setState("dirty");
    },
    [markSaved, onSave, settleAuthoritative],
  );

  const status =
    state === "saving" && unknown ? "CHECKING…" : statusLabels[state];
  return (
    <div className="grid gap-1">
      <Input
        {...props}
        value={draft}
        disabled={disabled}
        aria-busy={state === "saving" || undefined}
        className={cn(state === "error" && "border-danger", className)}
        onChange={(event) => {
          const next = event.target.value;
          if (savedTimerRef.current) {
            clearTimeout(savedTimerRef.current);
            savedTimerRef.current = null;
          }
          draftRef.current = next;
          setDraft(next);
          if (!savingRef.current) {
            setState(next.trim() === valueRef.current ? "clean" : "dirty");
          }
        }}
        onBlur={(event) => {
          onBlur?.(event);
          void save(event.currentTarget.value);
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
      <div
        aria-live="polite"
        className={cn(
          "min-h-4 font-mono text-[0.62rem] font-bold tracking-[0.08em]",
          state === "error"
            ? "text-danger"
            : state === "saved"
              ? "text-success"
              : "text-foreground-muted",
        )}
      >
        {state === "error" && emptyMessage ? emptyMessage : status}
      </div>
    </div>
  );
}
